import { Body1, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';

import { spacing } from '@/theme/tokens';
import { StatusPill, type StatusKind } from '@/design-system';
import type { ResourceHealth } from '@/api/types';

import { UptimeSparkline } from './UptimeSparkline';

const STATUS_TO_KIND: Record<ResourceHealth['status'], StatusKind> = {
  Healthy: 'healthy',
  Degraded: 'degraded',
  Unhealthy: 'unhealthy',
  Unknown: 'unknown',
};

const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: spacing.l,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.s,
    textAlign: 'left',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
  },
  cardButton: {
    cursor: 'pointer',
    width: '100%',
    font: 'inherit',
    color: 'inherit',
    appearance: 'none',
    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.s,
  },
  // Anchors `% + sparkline` to the bottom of the card so the same horizontal
  // band lines up across cards regardless of how many lines the name/meta
  // takes (e.g. `Microsoft.Storage/storageAccounts · global` wraps to two
  // lines; `Microsoft.Cdn/profiles · global` doesn't). The card grid stretches
  // every card in a row to the tallest one (CSS grid default), so once the
  // footer sits at `marginTop: auto`, the % numbers and graphs sit on the
  // same baseline across the row.
  footer: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.s,
  },
  // Left-hand side of the header. Must set `minWidth: 0` so a long
  // unbreakable resource name (e.g. `caredocsai-staging-southcentralus`)
  // doesn't push the right-hand StatusPill out of the visible card area —
  // flex items default to `min-width: auto`, which expands to the
  // content's intrinsic width and overflows the parent. Pair with
  // `overflowWrap: anywhere` on the name so long ids wrap inside the
  // available column instead of fighting the layout. The pill's own
  // `flexShrink: 0` lives in `StatusPill` itself for defense.
  headerLead: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  uptime: {
    fontSize: '20px',
    fontWeight: 600,
    fontFeatureSettings: '"tnum" 1',
    color: tokens.colorNeutralForeground1,
  },
  uptimeRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.s,
  },
});

interface ResourceCardProps {
  resource: ResourceHealth;
  /**
   * Caption rendered next to the headline percentage. Optional — defaults
   * to "30d uptime". The dashboard's window picker drives this so the
   * label matches the data ("7d uptime", "24h uptime", …) instead of
   * lying about a 30d window.
   */
  windowLabel?: string;
  onClick?: (resource: ResourceHealth) => void;
}

export function ResourceCard({ resource, windowLabel = '30d uptime', onClick }: ResourceCardProps) {
  const classes = useStyles();
  const status = STATUS_TO_KIND[resource.status];
  // The backend emits `uptimePct = 0` + `last30Days = []` whenever the
  // resource's uptime query returned no data (idle Cosmos, no availability
  // tests configured on App Insights, no agents heartbeating into the Log
  // Analytics workspace, etc.). Rendering that as `0.00%` lights every such
  // card up as a fleet-wide outage, which is misleading. Show an em-dash
  // when we genuinely have no signal; keep the numeric reading otherwise.
  const hasSignal = resource.last30Days.length > 0;
  const uptimeDisplay = hasSignal ? `${resource.uptimePct.toFixed(2)}%` : '—';

  const content = (
    <>
      <div className={classes.header}>
        <div className={classes.headerLead}>
          <Text weight="semibold" size={300}>
            {resource.name}
          </Text>
          <div className={classes.meta}>
            {resource.kind} · {resource.region}
          </div>
        </div>
        <StatusPill kind={status} />
      </div>
      <div className={classes.footer}>
        <div className={classes.uptimeRow}>
          <span className={classes.uptime}>{uptimeDisplay}</span>
          <Body1 className={classes.meta}>{windowLabel}</Body1>
        </div>
        <UptimeSparkline data={resource.last30Days} />
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={mergeClasses(classes.card, classes.cardButton)}
        onClick={() => {
          onClick(resource);
        }}
      >
        {content}
      </button>
    );
  }
  return <article className={classes.card}>{content}</article>;
}
